import { useCallback, useRef } from 'react';

import { useFloatingUI } from '../../hooks/use-floating-ui';

import type { DropdownContextValue } from './context';

export interface UseDropdownStateOptions {
  onSelect?: ((id: string) => void) | undefined;
  size?: 'sm' | 'md' | undefined;
  open?: boolean | undefined;
  onOpenChange?: ((open: boolean) => void) | undefined;
  minWidth?: number | undefined;
  maxWidth?: number | undefined;
  closeOnSelect?: boolean | undefined;
}

export function useDropdownState({
  onSelect,
  size = 'md',
  open: controlledOpen,
  onOpenChange,
  minWidth,
  maxWidth,
  closeOnSelect = true,
}: UseDropdownStateOptions): DropdownContextValue {
  const itemCountRef = useRef(0);

  // Reset item counter when dropdown opens (items will remount)
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        itemCountRef.current = 0;
      }
      onOpenChange?.(open);
    },
    [onOpenChange]
  );

  const {
    isOpen,
    setIsOpen,
    activeIndex,
    setReference,
    setFloating,
    floatingStyles,
    context,
    getReferenceProps,
    getFloatingProps,
    getItemProps,
    listRef,
  } = useFloatingUI({
    open: controlledOpen,
    onOpenChange: handleOpenChange,
    enableListNavigation: true,
    enableTypeahead: false,
    role: 'menu',
    width: 'none',
    minWidth,
    maxWidth,
  });

  const onItemSelect = useCallback(
    (id: string) => {
      onSelect?.(id);

      if (closeOnSelect) {
        setIsOpen(false);
      }
    },
    [onSelect, closeOnSelect, setIsOpen]
  );

  const registerItem = useCallback(() => {
    const index = itemCountRef.current;
    itemCountRef.current += 1;
    return index;
  }, []);

  return {
    isOpen,
    activeIndex,
    size,
    setIsOpen,
    onItemSelect,
    registerItem,
    setReference,
    setFloating,
    floatingStyles,
    context,
    getReferenceProps,
    getFloatingProps,
    getItemProps,
    listRef,
  };
}
